// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadSession from "@insite/client-framework/Store/Context/Handlers/LoadSession";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import auth0ChangeEmail from "@west/App/Handlers/AccountSettings/Auth0ChangeEmail";
import xLoadingOverlay from "@west/App/Handlers/XNG/LoadingOverlay";
import { getChangeEmailVerifiedToken } from "@west/App/Store/Selectors/Auth0Selectors";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const formId = "ChangeEmailForm";
const loadingId = "CHANGE_EMAIL_LOADING";

const StyledForm = getStyledWrapper("form");

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    return {
        accountSettingsPageLink: getPageLinkByPageType(state, "AccountSettingsPage"),
        config: getChangeEmailVerifiedToken(state),
    };
};

const mapDispatchToProps = {
    xLoadingOverlay,
    auth0ChangeEmail,
    loadSession,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ChangeEmailStyles {
    form?: InjectableCss;
    gridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    confirmEmailGridItem?: GridItemProps;
    submitGridItem?: GridItemProps;
    email?: TextFieldPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const changeEmailStyles: ChangeEmailStyles = {};

const styles = changeEmailStyles;
const ChangeEmail: FC<Props> = ({
    config,
    accountSettingsPageLink,
    xLoadingOverlay,
    auth0ChangeEmail,
    loadSession,
}) => {
    const [newEmail, setNewEmail] = useState("");
    const [confirmNewEmail, setConfirmNewEmail] = useState("");
    const history = useHistory();
    const toaster = useToaster();

    const newEmailChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const newEmail = event.currentTarget.value;
        setNewEmail(newEmail);
    };

    const confirmNewEmailChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const confirmNewEmail = event.currentTarget.value;
        setConfirmNewEmail(confirmNewEmail);
    };

    const redirectToAccountSettingsPage = () => {
        loadSession({
            isSessionUpdate: true,
            additionalQueryStringParameters: {
                timestamp: new Date().getTime(),
            },
            onComplete: result => {
                xLoadingOverlay({ key: "remove", value: loadingId });
                if (accountSettingsPageLink?.url) {
                    history.replace(`${accountSettingsPageLink?.url}?changeEmailSuccess=true`);
                }
            },
        });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!newEmail || !confirmNewEmail) {
            return;
        }

        if (newEmail !== confirmNewEmail) {
            return;
        }

        xLoadingOverlay({ key: "add", value: loadingId });
        auth0ChangeEmail({
            newEmail,
            idToken: config.idToken,
            state: config.state,
            onSuccess: _ => {
                redirectToAccountSettingsPage();
            },
            onError: _ => {
                xLoadingOverlay({ key: "remove", value: loadingId });
                toaster.addToast({ body: translate("Sorry there was a problem, try again"), messageType: "danger" });
            },
        });
    };

    const confirmNewEmailError =
        newEmail && confirmNewEmail && newEmail !== confirmNewEmail
            ? siteMessage("Custom_EmailCombination_DoesNotMatch")
            : "";

    return (
        <StyledForm {...styles.form} onSubmit={handleSubmit} data-test-selector="changeEmailForm">
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        label={translate("New Email Address")}
                        placeholder={translate("Enter New Email")}
                        type="email"
                        onChange={newEmailChangeHandler}
                        value={newEmail}
                        required={true}
                        data-test-selector="changeEmailForm_newEmail"
                        {...styles.email}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.confirmEmailGridItem}>
                    <TextField
                        label={translate("Confirm New Email Address")}
                        placeholder={translate("Confirm New Email")}
                        type="email"
                        onChange={confirmNewEmailChangeHandler}
                        value={confirmNewEmail}
                        error={confirmNewEmailError}
                        required={true}
                        data-test-selector="changeEmailForm_confirmNewEmail"
                        {...styles.email}
                        {...{ formId }}
                    />
                </GridItem>
                <GridItem {...styles.submitGridItem}>
                    <Button
                        {...styles.submitButton}
                        type="submit"
                        disabled={!newEmail || !confirmNewEmail || !!confirmNewEmailError}
                        aria-label={translate("Submit")}
                        data-test-selector="changeEmailForm_submit"
                    >
                        {translate("Submit")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ChangeEmail),
    definition: {
        group: "Change Email" as any,
        allowedContexts: ["ChangeEmailPage"],
    },
};

export default widgetModule;
